import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { DollarSign, FileText, Calendar, TrendingUp, Package, ShieldAlert, Clock } from 'lucide-react';

function Card({ icon: Icon, label, value, sub, color = 'text-nova-600', alert }) {
  return (
    <div className={`bg-white rounded-xl border p-5 ${alert ? 'border-red-300 ring-1 ring-red-200' : ''}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-gray-50 ${color}`}><Icon className="w-5 h-5" /></div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function AlertBadge({ level, count }) {
  if (!count) return null;
  const cls = level === 'critical'
    ? 'bg-red-100 text-red-700 border border-red-200'
    : 'bg-yellow-100 text-yellow-700 border border-yellow-200';
  const label = level === 'critical' ? '🔴 Order now' : '🟡 At buffer';
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/dashboard/stats').then(setStats);
  }, []);

  if (!stats) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const hasCriticalAlert = stats.totalAlerts > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          {user.role === 'admin' ? 'Dashboard' : `Welcome, ${user.full_name}`}
        </h1>
        {hasCriticalAlert && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-sm">
            <ShieldAlert className="w-4 h-4" />
            {stats.totalAlerts} material alert{stats.totalAlerts > 1 ? 's' : ''} — order required
          </div>
        )}
      </div>

      {/* Admin stats — financial */}
      {user.role === 'admin' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card
            icon={DollarSign}
            label="Collected This Month"
            value={fmt(stats.collectedThisMonth)}
            color="text-green-600"
          />
          <Card
            icon={Calendar}
            label="Scheduled This Month"
            value={stats.scheduledThisMonth?.count ?? 0}
            sub={fmt(stats.scheduledThisMonth?.value) + ' in jobs'}
            color="text-indigo-600"
          />
          <Card
            icon={Clock}
            label="Awaiting Payment"
            value={stats.awaitingPayment?.count ?? 0}
            sub={stats.awaitingPayment?.owing > 0 ? fmt(stats.awaitingPayment.owing) + ' owing' : 'All clear'}
            color={stats.awaitingPayment?.owing > 0 ? 'text-rose-500' : 'text-gray-400'}
            alert={stats.awaitingPayment?.owing > 0}
          />
          <Card
            icon={FileText}
            label="Active Pipeline"
            value={stats.activeSales}
            sub={`${stats.totalSales} total sales`}
          />
        </div>
      )}

      {/* Manager stats — operational only, no $ values */}
      {user.role === 'manager' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card
            icon={Calendar}
            label="Scheduled This Month"
            value={stats.scheduledThisMonth?.count ?? 0}
            sub="installs"
            color="text-indigo-600"
          />
          <Card
            icon={Clock}
            label="Awaiting Payment"
            value={stats.awaitingPayment?.count ?? 0}
            sub={stats.awaitingPayment?.count > 0 ? 'needs follow-up' : 'All clear'}
            color={stats.awaitingPayment?.count > 0 ? 'text-rose-500' : 'text-gray-400'}
            alert={stats.awaitingPayment?.count > 0}
          />
          <Card icon={FileText} label="Active Pipeline" value={stats.activeSales} sub={`${stats.totalSales} total sales`} />
          {hasCriticalAlert && (
            <Card icon={Package} label="Inventory Alerts" value={stats.totalAlerts} sub="items need ordering" color="text-red-500" alert />
          )}
        </div>
      )}

      {/* Salesman stats — commission only, no revenue figures */}
      {user.role === 'salesman' && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card icon={FileText} label="Your Active Sales" value={stats.activeSales} sub={`${stats.totalSales} total`} />
          <Card icon={DollarSign} label="Your Commission" value={fmt(stats.commissionEarned)} color="text-green-600" />
          {stats.managerCommission > 0 && (
            <Card icon={DollarSign} label="Manager Bonus" value={fmt(stats.managerCommission)} color="text-purple-600" />
          )}
        </div>
      )}

      {(user.role === 'admin' || user.role === 'manager') && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Jobs */}
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2"><Calendar className="w-4 h-4" /> Upcoming Installs</h2>
              <Link to="/schedule" className="text-sm text-nova-600 hover:underline">View all</Link>
            </div>
            <div className="divide-y">
              {stats.upcoming?.length === 0 && <p className="p-4 text-gray-400 text-sm">No upcoming installs</p>}
              {stats.upcoming?.map(job => (
                <div key={job.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{job.customer_name}</p>
                      <p className="text-sm text-gray-500">{job.color_name} · {job.total_footage}ft · {job.customer_city}</p>
                    </div>
                    <span className="text-sm font-medium text-nova-600">{job.scheduled_date}</span>
                  </div>
                  {job.payment_terms && (
                    <p className="text-xs mt-1 px-2 py-1 bg-amber-50 text-amber-700 rounded inline-block">{job.payment_terms}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Inventory Alerts — tiered */}
          <div className={`bg-white rounded-xl border ${hasCriticalAlert ? 'border-red-300' : ''}`}>
            <div className={`p-4 border-b flex items-center justify-between ${hasCriticalAlert ? 'bg-red-50' : ''}`}>
              <h2 className={`font-semibold flex items-center gap-2 ${hasCriticalAlert ? 'text-red-700' : ''}`}>
                <Package className="w-4 h-4" /> Inventory Alerts
                {hasCriticalAlert && <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded-full">{stats.totalAlerts}</span>}
              </h2>
              <Link to="/inventory" className="text-sm text-nova-600 hover:underline">Manage</Link>
            </div>
            <div className="divide-y">
              {/* General materials — critical (dipping into buffer) */}
              {stats.generalCritical?.map(item => (
                <div key={item.id} className="px-4 py-3 flex justify-between items-center bg-red-50">
                  <div>
                    <span className="text-sm font-medium text-red-800">{item.item_name}</span>
                    <p className="text-xs text-red-600">In stock: {item.qty_in_stock} · Buffer: {item.buffer_qty} · Short by {Math.abs(item.effective_available)}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-red-600 text-white rounded-full font-medium">Order now</span>
                </div>
              ))}

              {/* General materials — out of stock */}
              {stats.generalOutOfStock?.filter(i => !stats.generalCritical?.find(c => c.id === i.id)).map(item => (
                <div key={item.id} className="px-4 py-3 flex justify-between items-center bg-red-50">
                  <span className="text-sm font-medium text-red-800">{item.item_name}</span>
                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full border border-red-200">Out of stock</span>
                </div>
              ))}

              {/* General materials — at buffer threshold */}
              {stats.generalWarning?.map(item => (
                <div key={item.id} className="px-4 py-3 flex justify-between items-center bg-yellow-50">
                  <div>
                    <span className="text-sm font-medium text-yellow-800">{item.item_name}</span>
                    <p className="text-xs text-yellow-600">In stock: {item.qty_in_stock} · Buffer: {item.buffer_qty}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full border border-yellow-200">At buffer</span>
                </div>
              ))}

              {/* Channel colors that need ordering — with job detail */}
              {stats.channelNeedsOrder?.map(c => (
                <div key={c.id} className="px-4 py-3 bg-red-50">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-red-800">{c.color_name}</span>
                    <span className="text-xs px-2 py-0.5 bg-red-600 text-white rounded-full font-medium">
                      Order {c.shortfallCh} ch ({c.shortfallFt} ft)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {c.allJobs?.map(j => (
                      <span key={j.id} className="text-xs bg-white border border-red-200 text-red-700 px-1.5 py-0.5 rounded">
                        {j.customer_name} · {j.total_footage}ft
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {!hasCriticalAlert && !stats.generalWarning?.length && !stats.channelNeedsOrder?.length && (
                <p className="p-4 text-gray-400 text-sm">All stock levels healthy</p>
              )}
            </div>
          </div>

          {/* Sales by Salesman */}
          {stats.salesBySalesman?.length > 0 && (
            <div className="bg-white rounded-xl border lg:col-span-2">
              <div className="p-4 border-b"><h2 className="font-semibold">Sales by Salesman</h2></div>
              <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.salesBySalesman.map(s => (
                  <div key={s.full_name} className="text-center">
                    <p className="text-lg font-bold">{s.count}</p>
                    <p className="text-sm text-gray-500">{s.full_name}</p>
                    <p className="text-xs text-gray-400">{fmt(s.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
